/**
 * Cart Addon Protection
 * ----------------------
 * Protects addon line items in the cart drawer and cart page:
 * 1. Hides quantity controls and remove buttons on addon items
 * 2. Monkey-patches theme.cart.changeItem so removing/changing a
 *    parent automatically removes/syncs its addons in the SAME
 *    request (no timing issues, no flash, no checkout race)
 *
 * Addon items have these hidden properties (prefixed with _):
 *   _parent:      the _addon_key value of the parent line item
 *   _addon_group: which option group this addon belongs to
 *   _addon_for:   the parent product title (for display)
 *
 * Parent items have:
 *   _addon_key:   unique key matching child _parent values
 */

(function () {
  'use strict';

  var CART_JSON_URL = '/cart.js';
  var CART_UPDATE_URL = '/cart/update.js';

  /* ---- Cached cart data ---- */
  var cachedCartData = null;

  /**
   * Fetch the full cart JSON and build relationship maps.
   */
  function fetchCartData() {
    return fetch(CART_JSON_URL, {
      credentials: 'same-origin',
      headers: { 'Accept': 'application/json' }
    })
    .then(function (r) { return r.json(); })
    .then(function (cart) {
      cachedCartData = buildCartMaps(cart);
      return cachedCartData;
    });
  }

  function buildCartMaps(cart) {
    var parentsByKey = {};      // _addon_key → cart item
    var addonsByParent = {};    // _addon_key → [cart item, …]
    var parentByItemKey = {};   // cart line-item key → _addon_key (if parent)
    var addonItemKeys = new Set();

    cart.items.forEach(function (item) {
      var props = item.properties || {};
      if (props['_addon_key']) {
        parentsByKey[props['_addon_key']] = item;
        parentByItemKey[item.key] = props['_addon_key'];
      }
      if (props['_parent']) {
        if (!addonsByParent[props['_parent']]) {
          addonsByParent[props['_parent']] = [];
        }
        addonsByParent[props['_parent']].push(item);
        addonItemKeys.add(item.key);
      }
    });

    return {
      cart: cart,
      parentsByKey: parentsByKey,
      addonsByParent: addonsByParent,
      parentByItemKey: parentByItemKey,
      addonItemKeys: addonItemKeys
    };
  }

  /* ---- Monkey-patch theme.cart.changeItem ---- */

  function patchChangeItem() {
    if (typeof theme === 'undefined' || !theme.cart || !theme.cart.changeItem) {
      console.warn('[Addon Protection] theme.cart.changeItem not found, retrying…');
      setTimeout(patchChangeItem, 500);
      return;
    }

    var originalChangeItem = theme.cart.changeItem.bind(theme.cart);

    theme.cart.changeItem = function (key, qty) {
      // If we don't have cached data yet, fall through to original
      if (!cachedCartData) {
        return originalChangeItem(key, qty);
      }

      var addonKey = cachedCartData.parentByItemKey[key];

      // Not a parent item — just proceed normally
      if (!addonKey) {
        return originalChangeItem(key, qty);
      }

      var addons = cachedCartData.addonsByParent[addonKey] || [];
      if (addons.length === 0) {
        return originalChangeItem(key, qty);
      }

      // Build an updates object: { lineItemKey: newQty, … }
      var updates = {};
      updates[key] = qty;
      addons.forEach(function (addon) {
        updates[addon.key] = qty;
      });

      console.log('[Addon Protection] Batch update (parent + addons):', updates);

      // Use /cart/update.js which handles multiple items atomically
      return theme.cart._updateCart({
        url: CART_UPDATE_URL + '?t=' + Date.now(),
        data: JSON.stringify({ updates: updates })
      });
    };

    console.log('[Addon Protection] theme.cart.changeItem patched');
  }

  /* ---- DOM protection: hide controls on addon items ---- */

  function protectAddonItems() {
    fetchCartData().then(function (data) {
      document.querySelectorAll('.cart__item[data-key]').forEach(function (el) {
        var key = el.getAttribute('data-key');

        if (data.addonItemKeys.has(key)) {
          el.classList.add('cart__item--addon');

          // Hide quantity controls
          var qtyWrap = el.querySelector('.js-qty__wrapper');
          if (qtyWrap) qtyWrap.style.display = 'none';

          // Hide remove link
          var removeLink = el.querySelector('.cart__remove');
          if (removeLink) removeLink.style.display = 'none';
        }
      });
    });
  }

  /* ---- Event listeners ---- */

  // After the theme rebuilds the cart DOM, protect addon items and refresh cache
  document.addEventListener('cart:updated', function () {
    setTimeout(protectAddonItems, 300);
  });

  // After ajax add-to-cart
  document.addEventListener('ajaxProduct:added', function () {
    setTimeout(protectAddonItems, 500);
  });

  // Initial setup
  function init() {
    // Fetch initial cart data, then patch changeItem
    fetchCartData().then(function () {
      patchChangeItem();
      protectAddonItems();
    });
  }

  if (document.readyState !== 'loading') {
    setTimeout(init, 100);
  } else {
    document.addEventListener('DOMContentLoaded', init);
  }
})();
